// Smart Sash Admin Panel - Stream Player Component
// LiveKit video player wrapper for observer mode

import { Spinner } from '@/components/common';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  TrackReference,
  useConnectionState,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import { AudioReceiverStats, ConnectionState, Track, VideoReceiverStats } from 'livekit-client';
import React, { useEffect } from 'react';

import '@livekit/components-styles';

export interface VideoStreamInfo {
  participantIdentity: string;
  participantName?: string;
  participantSid: string;
  source: Track.Source;
  trackName: string;
  trackSid: string;
  mimeType?: string;
  dimensions?: Track.Dimensions;
  streamState?: Track.StreamState;
  isMuted: boolean;
  isSubscribed: boolean;
  simulcasted: boolean;
  currentBitrate?: number;
  stats?: VideoReceiverStats;
  mediaSettings?: MediaTrackSettings;
}

export interface AudioStreamInfo {
  participantIdentity: string;
  participantName?: string;
  participantSid: string;
  source: Track.Source;
  trackName: string;
  trackSid: string;
  mimeType?: string;
  isMuted: boolean;
  isSubscribed: boolean;
  currentBitrate?: number;
  stats?: AudioReceiverStats;
  mediaSettings?: MediaTrackSettings;
  audioLevel: number;
  isSpeaking: boolean;
}

interface StreamPlayerProps {
  wsUrl: string;
  token: string;
  isMuted: boolean;
  volume: number;
  onConnected: () => void;
  onDisconnected: () => void;
  onVideoInfoChange?: (info: VideoStreamInfo | null) => void;
  onAudioInfoChange?: (info: AudioStreamInfo | null) => void;
}

type StatsCapableVideoTrack = {
  currentBitrate?: number;
  mediaStreamTrack?: MediaStreamTrack;
  sid?: string;
  streamState?: Track.StreamState;
  getReceiverStats: () => Promise<VideoReceiverStats | undefined>;
};

const hasReceiverStats = (track: unknown): track is StatsCapableVideoTrack => {
  return typeof (track as StatsCapableVideoTrack | undefined)?.getReceiverStats === 'function';
};

type StatsCapableAudioTrack = {
  currentBitrate?: number;
  mediaStreamTrack?: MediaStreamTrack;
  sid?: string;
  getReceiverStats: () => Promise<AudioReceiverStats | undefined>;
};

const hasAudioReceiverStats = (track: unknown): track is StatsCapableAudioTrack => {
  return typeof (track as StatsCapableAudioTrack | undefined)?.getReceiverStats === 'function';
};

const buildVideoStreamInfo = async (trackRef: TrackReference): Promise<VideoStreamInfo> => {
  const { participant, publication, source } = trackRef;
  const videoTrack = publication.videoTrack;
  const stats = hasReceiverStats(videoTrack) ? await videoTrack.getReceiverStats() : undefined;

  return {
    participantIdentity: participant.identity,
    participantName: participant.name,
    participantSid: participant.sid,
    source,
    trackName: publication.trackName,
    trackSid: publication.trackSid || videoTrack?.sid || '',
    mimeType: publication.mimeType || stats?.mimeType,
    dimensions: publication.dimensions,
    streamState: videoTrack?.streamState,
    isMuted: publication.isMuted,
    isSubscribed: publication.isSubscribed,
    simulcasted: Boolean(publication.simulcasted),
    currentBitrate: videoTrack?.currentBitrate,
    stats,
    mediaSettings: videoTrack?.mediaStreamTrack?.getSettings(),
  };
};

const buildAudioStreamInfo = async (trackRef: TrackReference): Promise<AudioStreamInfo> => {
  const { participant, publication, source } = trackRef;
  const audioTrack = publication.audioTrack;
  const stats = hasAudioReceiverStats(audioTrack) ? await audioTrack.getReceiverStats() : undefined;

  return {
    participantIdentity: participant.identity,
    participantName: participant.name,
    participantSid: participant.sid,
    source,
    trackName: publication.trackName,
    trackSid: publication.trackSid || audioTrack?.sid || '',
    mimeType: publication.mimeType,
    isMuted: publication.isMuted,
    isSubscribed: publication.isSubscribed,
    currentBitrate: audioTrack?.currentBitrate,
    stats,
    mediaSettings: audioTrack?.mediaStreamTrack?.getSettings(),
    audioLevel: participant.audioLevel,
    isSpeaking: participant.isSpeaking,
  };
};

const VideoRenderer: React.FC<{
  isMuted: boolean;
  volume: number;
  onVideoInfoChange?: (info: VideoStreamInfo | null) => void;
  onAudioInfoChange?: (info: AudioStreamInfo | null) => void;
}> = ({ isMuted, volume, onVideoInfoChange, onAudioInfoChange }) => {
  const videoTracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], {
    onlySubscribed: true,
  });
  const audioTracks = useTracks([Track.Source.Microphone, Track.Source.ScreenShareAudio], {
    onlySubscribed: true,
  });
  const connectionState = useConnectionState();
  const videoTrack = videoTracks[0];
  const audioTrack = audioTracks[0];

  useEffect(() => {
    if (!onVideoInfoChange) return;

    if (!videoTrack) {
      onVideoInfoChange(null);
      return;
    }

    let cancelled = false;

    const updateVideoInfo = async () => {
      const info = await buildVideoStreamInfo(videoTrack);
      if (!cancelled) onVideoInfoChange(info);
    };

    updateVideoInfo();
    const intervalId = window.setInterval(updateVideoInfo, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [videoTrack, onVideoInfoChange]);

  useEffect(() => {
    if (!onAudioInfoChange) return;

    if (!audioTrack) {
      onAudioInfoChange(null);
      return;
    }

    let cancelled = false;

    const updateAudioInfo = async () => {
      const info = await buildAudioStreamInfo(audioTrack);
      if (!cancelled) onAudioInfoChange(info);
    };

    updateAudioInfo();
    const intervalId = window.setInterval(updateAudioInfo, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [audioTrack, onAudioInfoChange]);

  if (connectionState === ConnectionState.Connecting) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-dark text-white">
        <Spinner />
        <p className="mt-3">Connecting to stream...</p>
      </div>
    );
  }

  if (videoTracks.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-dark text-white">
        <i className="fas fa-video-slash fa-3x mb-3 text-muted" />
        <p className="text-muted">Waiting for presenter video...</p>
      </div>
    );
  }

  return (
    <div className="position-relative w-100 h-100 bg-dark">
      <VideoTrack
        trackRef={videoTrack}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      <RoomAudioRenderer muted={isMuted} volume={volume} />
    </div>
  );
};

export const StreamPlayer: React.FC<StreamPlayerProps> = ({
  wsUrl,
  token,
  isMuted,
  volume,
  onConnected,
  onDisconnected,
  onVideoInfoChange,
  onAudioInfoChange,
}) => {
  return (
    <LiveKitRoom
      serverUrl={wsUrl}
      token={token}
      connect={true}
      audio={false}
      video={false}
      onConnected={onConnected}
      onDisconnected={onDisconnected}
      options={{
        adaptiveStream: true,
        dynacast: true,
        disconnectOnPageLeave: true,
      }}
      style={{ height: '100%' }}
    >
      <VideoRenderer
        isMuted={isMuted}
        volume={volume}
        onVideoInfoChange={onVideoInfoChange}
        onAudioInfoChange={onAudioInfoChange}
      />
    </LiveKitRoom>
  );
};
